import { Input } from "@/components/ui/input";
import { NumInput, Panel, YNToggle } from "@/components/FieldInput";
import { ProductPicker } from "@/components/ProductPicker";
import { REC_CATEGORY } from "@/lib/api";
import { FINAL_ROWS } from "@/lib/schema";
import { TIPS } from "@/lib/tips";
import { Tip } from "@/components/Explain";
import { money } from "@/lib/format";

const Table = ({ title, people, rows, testId }) => (
  <Panel title={title} testId={testId}>
    <div className="ins-table" style={{ "--cols": people.length }}>
      <div className="ins-row ins-head"><span />{people.map((p) => <span key={p.key}>{p.label}</span>)}</div>
      {rows.map((r) => (
        <div key={r.label} className={`ins-row ${r.strong ? "is-strong" : ""} ${r.sub ? "is-sub" : ""} ${r.rec ? "is-rec" : ""}`}>
          <span className="inline-flex items-center gap-1">{r.label}{r.hint && <em className="field-hint">{r.hint}</em>}<Tip text={TIPS[r.label]} /></span>
          {people.map((p) => <span key={p.key}>{r.cell(p)}</span>)}
        </div>
      ))}
    </div>
  </Panel>
);

export const InsuranceTab = ({ d, s, set, cur, products = [], clientMode = false }) => {
  const m = (v) => <span className="font-mono">{money(v, cur)}</span>;
  const gap = (v, id) => <span className={`font-mono font-bold ${v > 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid={id}>{v > 0 ? money(v, cur) : "Covered"}</span>;
  const num = (field, decimals = false) => (p) => <NumInput id={`ins-${p.key}-${field}`} decimals={decimals} value={d.insurance[p.key][field]} onChange={(v) => set(`insurance.${p.key}.${field}`, v)} />;
  const yn = (field) => (p) => <YNToggle id={`ins-${p.key}-${field}`} value={d.insurance[p.key][field]} onChange={(v) => set(`insurance.${p.key}.${field}`, v)} />;
  const recRows = (key) => [
    { label: "Recommended product", rec: true, cell: (p) => (
      <div className="flex gap-1 w-full">
        <Input data-testid={`rec-${p.key}-${key}-product`} placeholder="Product name" value={d.recommendations[p.key][key].product} onChange={(e) => set(`recommendations.${p.key}.${key}.product`, e.target.value)} className="bg-white h-9 min-w-0" />
        <ProductPicker id={`rec-${p.key}-${key}-pick`} products={products} category={REC_CATEGORY[key]} cur={cur}
          onPick={(prod) => set(`recommendations.${p.key}.${key}`, { product: prod.name, premium: prod.premium })} />
      </div>
    ) },
    { label: "Monthly premium", rec: true, cell: (p) => <NumInput id={`rec-${p.key}-${key}-premium`} placeholder="$ / month" value={d.recommendations[p.key][key].premium} onChange={(v) => set(`recommendations.${p.key}.${key}.premium`, v)} /> },
  ];
  const people = [
    { key: "client", label: d.profile.name || "Client", r: s.ins.client },
    ...(s.ins.hasSpouse ? [{ key: "spouse", label: d.profile.spouse.name || "Spouse", r: s.ins.spouse }] : []),
  ];
  const F = d.insurance.finals;
  return (
    <div className="tab-grid">
      <Table title="Life insurance" testId="panel-life" people={people} rows={[
        { label: "Annual income", hint: "Tab 2", cell: (p) => m(p.r.income) },
        { label: "% of income to replace", cell: num("incomePct", true) },
        { label: "Years of support needed", cell: num("yearsNeeded", true) },
        { label: "Use income replacement method", cell: yn("incomeMethod") },
        { label: "Use family expenses method", hint: "expenses excl. mortgage × years", cell: yn("expenseMethod") },
        { label: "Income replacement need", sub: true, cell: (p) => m(p.r.incomeRepl ?? 0) },
        { label: "Family & dependant needs", sub: true, cell: (p) => m(p.r.familyNeeds ?? 0) },
        { label: "Mortgage & liabilities", hint: "Tab 2, marked Insure", sub: true, cell: (p) => m(p.r.liabilities) },
        { label: "Other obligations", hint: "final expenses below", sub: true, cell: (p) => m(p.r.obligations) },
        { label: "Total coverage requirement", strong: true, cell: (p) => <span className="font-mono" data-testid={`ins-${p.key}-total-need`}>{money(p.r.totalNeed, cur)}</span> },
        { label: "Existing permanent life", cell: num("permanentLife") },
        { label: "Existing term life", cell: num("termLife") },
        { label: "Existing group life", cell: num("groupLife") },
        { label: "Less existing coverage", sub: true, cell: (p) => m(-p.r.existingLife) },
        { label: "Less assets available at death", hint: "marked in Tabs 2 & 3", sub: true, cell: (p) => m(-p.r.availableAssets) },
        { label: "Additional coverage required", strong: true, cell: (p) => gap(p.r.lifeAdditional, `ins-${p.key}-life-additional`) },
          ...(clientMode ? [] : recRows("life")),
      ]} />
      <div className="space-y-4">
        <Table title="Disability insurance (monthly)" testId="panel-disability" people={people} rows={[
          { label: "% of income to replace", cell: num("diPct", true) },
          { label: "Monthly requirement", sub: true, cell: (p) => m(p.r.diRequired) },
          { label: "Group LTD benefit / month", cell: num("groupLtd") },
          { label: "Individual disability / month", cell: num("individualDi") },
          { label: "Monthly coverage shortfall", strong: true, cell: (p) => gap(p.r.diShortfall, `ins-${p.key}-di-shortfall`) },
          ...(clientMode ? [] : recRows("di")),
        ]} />
        <Table title="Critical illness" testId="panel-ci" people={people} rows={[
          { label: "Years of income", hint: "Excel default 1", cell: num("ciYears", true) },
          { label: "Estimated requirement", sub: true, cell: (p) => m(p.r.ciRequired) },
          { label: "Existing coverage", cell: num("criticalIllness") },
          { label: "Additional protection needed", strong: true, cell: (p) => gap(p.r.ciAdditional, `ins-${p.key}-ci-additional`) },
          ...(clientMode ? [] : recRows("ci")),
        ]} />
      </div>
      <Panel title="Other obligations (final expenses, household)" className="col-span-full" testId="panel-finals">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {FINAL_ROWS.map(([k, label]) => (
            <div key={k} className="space-y-1">
              <span className="field-label">{label}</span>
              <div className="flex gap-1.5">
                <NumInput id={`final-${k}-amount`} value={F[k]?.amount} onChange={(v) => set(`insurance.finals.${k}.amount`, v)} />
                <YNToggle id={`final-${k}-use`} value={F[k]?.use} onChange={(v) => set(`insurance.finals.${k}.use`, v)} />
              </div>
            </div>
          ))}
        </div>
        {!clientMode && <div className="flex flex-wrap items-center justify-between gap-2 mt-3 rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-sm">
          <span className="text-slate-700">Recommended plan: <b>{s.plan.rows.length}</b> product(s) entered (incl. retirement in Tab 3)</span>
          <span className="font-semibold text-slate-900">Total monthly premium <span className="font-mono" data-testid="plan-total-premium">{money(s.plan.totalPremium, cur)}</span></span>
        </div>}
        <p className="text-[11px] text-slate-500 mt-2">Each existing policy and each asset is subtracted once. Critical illness and disability coverage are not used to reduce the life insurance need.</p>
      </Panel>
    </div>
  );
};
